import '@docs/kd-d2c-components/es/index.css'; // kd组件库全局css
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { useLayoutStore } from './stores/useLayoutStore';
// 导入 URL.parse polyfill 以支持 Electron webview 环境
import { Loading } from '@docs/kd-d2c-components';
import { AIWindowProvider } from './components/pc/Context/context';
import { GlobalSchedulerModalTrigger } from './components/pc/SchedulerModal/GlobalSchedulerModalTrigger';
import { Sidebar } from './components/pc/sidebar';
import { TopHeader } from './components/pc/sidebar/components/header';
import intl from './internationalzation/intl';
import ClawHome from './pages/pc/claw/index';
import { userInfoReady } from './stores/userModel';
import { useTheme } from './stores/useTheme';
import './utils/url-polyfill';
import { isMobile } from './utils/user-agent';
import SkillsPersistentIframe from './pages/pc/skills/SkillsPersistentIframe';
// 导入VConsole调试工具
import '@kim/ui/dist/index.css';
import { theme as antdTheme, ConfigProvider } from 'antd';
import 'rc-tooltip/assets/bootstrap.css';
import { SearchConfigContextProvider } from './common/context/SearchConfigContext';
import { useClawBotStatus } from './hooks/useClawBotStatus';
import { initWeblogger } from './utils/logger';
import './index.less';
import './utils/window-methods';
import {
  LEGACY_CLAW_ROUTE_BASE,
  MAINAGENT_CHAT_PATH,
  MAINAGENT_DASHBOARD_PATH,
  MAINAGENT_ROUTE_BASE,
  normalizeLegacyClawPath,
} from './utils/mainagent-route';
const rootEl = document.getElementById('root');

initWeblogger({
  env: process.env.NODE_ENV === 'production' ? 'production' : 'development',
  productName: 'KIM_DESKTOP',
});

const KIM_BUILD_NUMBER = 30000;
// const KIM_BUILD_NUMBER = 59641;
// const KIM_BUILD_NUMBER = 10000;
const MarioHome = lazy(
  () =>
    isMobile
      ? import('./pages/h5/home') // ⬅️ 动态加载 H5 组件
      : import('./pages/pc/home'), // ⬅️ 动态加载 PC 组件
);

// const ClawHome = lazy(() => import('./pages/pc/claw/index'));
const ClawChat = lazy(() => import('./pages/pc/claw/chat/index'));
const ClawDashboard = lazy(() => import('./pages/pc/claw/dashboard/index'));
const History = lazy(() => import('./pages/h5/history'));
const ScheduledTask = lazy(() => import('./pages/h5/scheduled-task'));
const ScheduledTaskCreate = lazy(
  () => import('./pages/h5/scheduled-task/create'),
);
const ScheduledTaskEdit = lazy(() => import('./pages/h5/scheduled-task/edit'));
const Files = lazy(() => import('./pages/h5/files'));
const ConversationDetail = lazy(() =>
  isMobile
    ? import('./pages/h5/conversation-detail')
    : import('./pages/pc/conversation-detail'),
);

const Preview = lazy(() => import('./pages/h5/preview'));
const NotFound = lazy(() => import('./pages/notFound'));
const AgentDebug = lazy(() => import('./pages/pc/agentDebug'));

const SkillsLayout = lazy(() => import('./pages/pc/skills'));
const CollaborativeLab = lazy(() => import('./pages/pc/collaborative-lab'));
const AgentsList = lazy(() => import('./pages/pc/agents/list'));
const AgentsForm = lazy(() => import('./pages/pc/agents/Form'));
const MessageShare = lazy(() => import('./pages/pc/message-share'));
const KimAgentList = lazy(() => import('./pages/pc/kim-agent-list'));

/** 壳地址误为 /skills-official 时（与 iframe 内路由混淆），统一跳转到 /skills 壳路径 */
function SkillsOfficialShellRedirect() {
  const { pathname, search, hash } = useLocation();
  const suffix = pathname.startsWith('/skills-official/')
    ? pathname.slice('/skills-official'.length)
    : '';
  const shellPath = suffix ? `/skills${suffix}` : '/skills';
  return <Navigate to={{ pathname: shellPath, search, hash }} replace />;
}

function LegacyClawShellRedirect() {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      to={{ pathname: normalizeLegacyClawPath(pathname), search, hash }}
      replace
    />
  );
}

// ─── 路由内容（需要在 BrowserRouter 内才能使用 useLocation）────────────────────
interface AppRoutesProps {
  hideWebSidebar: boolean;
  sidebarExpand: boolean;
  setSidebarExpand: (v: boolean) => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  hideWebSidebar,
  sidebarExpand,
  setSidebarExpand,
}) => {
  const location = useLocation();
  const isHideSidebarPage = ['/kim-agent-list', '/message-share'].some((path) =>
    location.pathname.startsWith(path),
  );
  return (
    <>
      {!isHideSidebarPage && !hideWebSidebar && (
        <AIWindowProvider>
          {sidebarExpand ? (
            <div className="mario-sidebar">
              <Sidebar onFoldClick={() => setSidebarExpand(false)} />
            </div>
          ) : (
            <TopHeader onFoldClick={() => setSidebarExpand(true)} />
          )}
        </AIWindowProvider>
      )}
      <div className="mario-main-content">
        <Suspense fallback={null}>
          <Routes>
            {/* 2026-04: PC Claw 页面默认入口改到 /mainagent；兼容旧 /claw */}
            <Route
              path="/"
              element={
                <Navigate
                  to={isMobile ? '/mario' : MAINAGENT_ROUTE_BASE}
                  replace
                />
              }
            />
            <Route
              path={LEGACY_CLAW_ROUTE_BASE}
              element={<LegacyClawShellRedirect />}
            />
            <Route
              path={`${LEGACY_CLAW_ROUTE_BASE}/*`}
              element={<LegacyClawShellRedirect />}
            />
            <Route
              path="/skills-official"
              element={<SkillsOfficialShellRedirect />}
            />
            <Route
              path="/skills-official/*"
              element={<SkillsOfficialShellRedirect />}
            />
            <Route path="/mario" element={<MarioHome />} />
            <Route path={MAINAGENT_ROUTE_BASE} element={<ClawHome />} />
            <Route
              path={MAINAGENT_DASHBOARD_PATH}
              element={<ClawDashboard />}
            />
            <Route
              path={`${MAINAGENT_ROUTE_BASE}/:sessionKey`}
              element={<ClawHome />}
            />
            <Route path={MAINAGENT_CHAT_PATH} element={<ClawChat />} />
            <Route path="/history" element={<History />} />
            <Route path="/scheduled-task" element={<ScheduledTask />} />
            <Route
              path="/scheduled-task/create"
              element={<ScheduledTaskCreate />}
            />
            <Route
              path="/scheduled-task/edit"
              element={<ScheduledTaskEdit />}
            />
            <Route path="/files/:conversationId" element={<Files />} />
            <Route
              path="/c/:conversationId"
              element={<ConversationDetail hideWebSidebar={hideWebSidebar} />}
            />
            <Route path="/preview" element={<Preview />} />
            <Route path="/agentDebug" element={<AgentDebug />} />
            {/* /skills/* 路由：本地开发时由 SkillsLayout 处理跳转；生产环境 iframe 由持久容器接管 */}
            <Route path="/skills/*" element={<SkillsLayout />} />
            <Route
              path="/lab"
              element={<CollaborativeLab hideWebSidebar={!sidebarExpand} />}
            />
            <Route path="/agents">
              <Route
                index
                element={<AgentsList hideWebSidebar={!sidebarExpand} />}
              />
              <Route path="form/:type/:id?" element={<AgentsForm />} />
            </Route>
            {/* 消息分享页（无侧边栏） */}
            <Route path="/message-share/:shareId" element={<MessageShare />} />
            <Route path="/kim-agent-list/:groupId" element={<KimAgentList />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        <SkillsPersistentIframe />
      </div>
    </>
  );
};

const App = () => {
  const [ready, setReady] = useState(false);
  const { initTheme, currentTheme } = useTheme();
  const searchParams = new URLSearchParams(window.location.search);
  const isReplay = searchParams.has('replay');
  const sidebarExpand = useLayoutStore((s) => s.sidebarExpand);
  const setSidebarExpand = useLayoutStore((s) => s.setSidebarExpand);

  useEffect(() => {
    // 初始化VConsole调试工具（仅在移动端开发环境）
    //initVConsole();
    const cleanupTheme = initTheme();
    userInfoReady.then(() => {
      setReady(true);
    });
    return cleanupTheme;
  }, [initTheme]);

  useEffect(() => {
    if (isReplay) setSidebarExpand(false);
  }, [isReplay, setSidebarExpand]);

  useClawBotStatus({ polling: false });

  useEffect(() => {
    let isUnmounted = false;
    function changeLanguage(language: string) {
      if (isUnmounted) return;
      const targetLanguage = language.includes('en') ? 'en-US' : 'zh-CN';
      const currentLanguage = intl.getLanguage();
      // 只有当目标语言与当前语言不同时才设置语言，避免死循环
      if (targetLanguage !== currentLanguage) {
        intl.setLanguage(targetLanguage);
      }
    }
    if (isMobile) {
      import('@src/utils/kim-bridge').then(({ kimBridge }) => {
        kimBridge.getKimStatus().then((res) => {
          changeLanguage(res.language || 'zh-CN');
        });
      });
    } else if (
      typeof window.KIM !== 'undefined' &&
      typeof window.KIM.getSystemSetting === 'function'
    ) {
      window.KIM.getSystemSetting().then((res) => {
        changeLanguage(res.language);
      });
    }

    return () => {
      isUnmounted = true;
    };
  }, []);

  const kimBuildNumber = Number(window.KIM?.kimVersion?.split('-')[1]) || 0;

  const hideWebSidebar =
    isMobile || (window.KIM && kimBuildNumber < KIM_BUILD_NUMBER);

  useEffect(() => {
    // 预加载所有路由模块
    const preloadRoutes = () => {
      if (isMobile) {
        import('./pages/h5/home');
        import('./pages/h5/history');
        import('./pages/h5/scheduled-task');
        import('./pages/h5/scheduled-task/create');
        import('./pages/h5/scheduled-task/edit');
        import('./pages/h5/files');
        import('./pages/h5/conversation-detail');
        import('./pages/h5/preview');
      } else {
        import('./pages/pc/home');
        import('./pages/pc/conversation-detail');
        import('./pages/pc/agentDebug');
        import('./pages/pc/skills');
      }
      import('./pages/notFound');
    };

    // 使用 setTimeout 确保在空闲时间预加载
    setTimeout(preloadRoutes, 0);
  }, []);

  const antdThemeConfig = useMemo(
    () => ({
      algorithm:
        currentTheme === 'dark'
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: '#0C63FA',
      },
    }),
    [currentTheme],
  );

  return (
    <ConfigProvider
      autoInsertSpaceInButton={false}
      theme={antdThemeConfig}
      locale={intl.getAntdLanguage()}
    >
      <BrowserRouter>
        <SearchConfigContextProvider>
          {ready ? (
            <div className="mario-entry CloudWhite">
              <GlobalSchedulerModalTrigger />
              <AppRoutes
                hideWebSidebar={hideWebSidebar}
                sidebarExpand={sidebarExpand}
                setSidebarExpand={setSidebarExpand}
              />
            </div>
          ) : (
            <Loading className="mario-loading" />
          )}
        </SearchConfigContextProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
};

if (rootEl) {
  const root = ReactDOM.createRoot(rootEl);
  root.render(<App />);
}
